/**
 * Game mode + opening — orthogonal rule modifiers layered on top of a variant.
 *
 * These are independent of `GameVariant`: any variant can be played in any mode
 * with any opening. `gameMode` changes the win condition (read by the engine's
 * `determineGameStatus`); `opening` only affects how the game is set up.
 */

/**
 * Game modifier:
 * - `normal`        — standard rules.
 * - `breakthrough`  — the first player to promote a man to king wins immediately.
 * - `antidraughts`  — misère/suicide: the player who would normally lose wins
 *                     (no moves left, or no pieces left). Captures stay mandatory.
 * - `blind`         — blindfold: the pieces are drawn fully transparent and every
 *                     visual aid that would give their squares away is suppressed.
 *                     THE ONLY MODE THAT CHANGES NO RULE: the engine plays it
 *                     exactly like `normal`, and everything that asks "are these
 *                     the standard rules?" must go through `hasStandardRules`
 *                     rather than compare against `'normal'`.
 */
export type GameMode = 'normal' | 'breakthrough' | 'antidraughts' | 'blind';

/** Selectable modes, ordered as shown in the challenge dropdown. */
export const GAME_MODES: readonly GameMode[] = ['normal', 'breakthrough', 'antidraughts', 'blind'] as const;

/** Default mode — standard rules. */
export const DEFAULT_GAME_MODE: GameMode = 'normal';

/**
 * True for the modes played with the STANDARD RULES. `blind` is one of them: it
 * changes how the board is DRAWN, not who wins.
 *
 * It exists to keep two different questions apart, which a bare `=== 'normal'`
 * conflates: "is this the plain mode?" (a UI question — does it deserve a badge)
 * and "are these the standard rules?" (what the engine's heuristics and the bots
 * actually care about). Reading the first where the second was meant is what
 * would silently change the AI's draw acceptance in a blindfold game, and make
 * the bots decline a game they can play perfectly well.
 *
 * Takes a plain string, for the same reason `variantHasBallot` does: it is asked the
 * question about values that arrive OFF THE WIRE, where a stale client can send
 * anything. `undefined` reads as standard — a board serialized before the modes
 * existed carries no mode and plays by the standard rules — but an unrecognised
 * mode reads as NON-standard, which is the safe answer: a bot that does not know
 * what it is being asked to play should decline, not sit down.
 */
export function hasStandardRules(mode: string | undefined | null): boolean {
  return mode === undefined || mode === null || mode === 'normal' || mode === 'blind';
}

/**
 * The modes that earn a badge. Stored games carry it as `NonStandardGameMode | null`,
 * where `null` is "a normal game" or a row saved before the modes existed.
 */
export type NonStandardGameMode = Exclude<GameMode, 'normal'>;

/**
 * Opening selection — how the first half-moves (semi-mosse) are decided before
 * the players take over:
 * - `free`      — players choose their own moves from the start.
 * - `random1..3` — that many random legal half-moves, drawn from EVERY legal move
 *              with no cap on how lopsided the result may be. Offered on every
 *              variant; the number of distinct sequences each one can produce is
 *              variant-specific (see `OPENING_SEQUENCE_COUNTS`).
 * - `ballot1..4` — 3 half-moves drawn from the official F.I.D. ballot, capped at
 *              that strength level: `ballot1` only even/±1 openings, `ballot4` the
 *              whole general table. Italian and English draughts only — every other
 *              variant has no ballot and falls back to random plies.
 * - `single`   — THE ONE THAT DOES NOT DRAW. A single opening, chosen by hand from
 *              the official table, played by everybody in every round: a themed
 *              tournament. Tournaments only, and only where a table exists.
 *
 * The ceilings line up exactly with the federal tables (verified against the
 * regulation): 1 → table C, 2 → table B, 3 → A ∪ B (Italian) / A (English),
 * 4 → the general table of 174.
 *
 * ── Why `single` needs a companion, and the other eight do not ───────────────
 *
 * The other eight say everything about themselves in one word: given the mode and
 * the variant, the drawer knows what to do. `single` cannot — WHICH of the 174 is
 * a second fact, and a word has nowhere to put it. It travels beside the mode as
 * `tournaments.opening_id` (migration 112), and the pair is only ever complete on
 * a tournament row: that is exactly why this mode never reaches a challenge, where
 * there is no column to carry the number (see `normalizeOpeningMode`).
 */
export type OpeningMode =
  | 'free'
  | 'random1'
  | 'random2'
  | 'random3'
  | 'ballot1'
  | 'ballot2'
  | 'ballot3'
  | 'ballot4'
  | 'single';

/** Strength ceiling of a ballot draw: no opening stronger than this is drawn. */
export type BallotCeiling = 1 | 2 | 3 | 4;

/**
 * Selectable openings, ordered as shown in the challenge dropdown: free first,
 * then the random draws and the ballots, both by widening pool. Reading down the
 * list, each entry constrains the start less than the one before it.
 *
 * EIGHT, and `single` is deliberately not among them: this list is what a CHALLENGE
 * offers, and a challenge has nowhere to carry the chosen opening's number. The
 * tournament list is `tournamentOpeningModesFor`.
 */
export const OPENING_MODES: readonly OpeningMode[] = [
  'free',
  'random1',
  'random2',
  'random3',
  'ballot1',
  'ballot2',
  'ballot3',
  'ballot4',
] as const;

/** Ballot modes, smallest pool first — the tail of OPENING_MODES. */
export const BALLOT_OPENING_MODES: readonly OpeningMode[] = ['ballot1', 'ballot2', 'ballot3', 'ballot4'] as const;

/** Default opening — free move. */
export const DEFAULT_OPENING_MODE: OpeningMode = 'free';

/** Number of random legal plies applied as a drawn opening on variants without a ballot table. */
export const RANDOM_OPENING_PLIES = 3;

/**
 * Half-moves in every entry of the official ballot tables (Italian FID, English
 * ACF): 1st White, 1st Black, 2nd White. A drawn opening of any other length has
 * no ballot to draw from and falls back to random legal plies.
 */
export const BALLOT_OPENING_PLIES = 3;

/**
 * True for any drawn opening, whatever its length. Derived from `openingPlies` so
 * the two can never disagree — and so an untyped value off the wire (a stale
 * client sending `undefined`) reads as "not drawn" instead of "drawn".
 */
export function isDrawnOpening(opening: OpeningMode): boolean {
  return openingPlies(opening) > 0;
}

/**
 * Half-moves the opening draws — 0 for a free opening, and 0 for anything this
 * build does not recognise, so an untyped value off the wire reads as "not drawn".
 */
export function openingPlies(opening: OpeningMode): 0 | 1 | 2 | 3 {
  if (opening === 'random1') return 1;
  if (opening === 'random2') return 2;
  /* `single` is three like the ballots, and for the same reason: it IS a ballot
     row, just named instead of drawn. This is also what makes `isDrawnOpening`
     answer true for it, which is what the banner, `getPlayedMoveCount` and the
     PDN move count all read — none of them cares HOW the opening was decided,
     only that those three half-moves were not played by the two at the board. */
  if (opening === 'random3' || opening === 'single' || ballotCeiling(opening) !== null) return BALLOT_OPENING_PLIES;
  return 0;
}

/**
 * Strength ceiling of a ballot mode, `null` for the free and random openings.
 * Tolerates a missing or unknown value the same way `isDrawnOpening` does:
 * settings stored before this option existed replay through here without an
 * `opening` at all.
 */
export function ballotCeiling(opening: OpeningMode): BallotCeiling | null {
  if (typeof opening !== 'string' || !opening.startsWith('ballot')) return null;
  const ceiling = Number(opening.slice(6));
  return ceiling >= 1 && ceiling <= 4 ? (ceiling as BallotCeiling) : null;
}

/**
 * True for the two variants with an official F.I.D. ballot table. Everything else
 * can still be played with a drawn opening, but the half-moves are random.
 *
 * Takes a plain string, case-insensitively, because both spellings are in play:
 * the server holds a `GameVariant` (`'ITALIAN'`) while the challenge UI and the
 * wire carry the lowercase slug (`'italian'`).
 */
export function variantHasBallot(variant: string): boolean {
  const v = variant.toLowerCase();
  return v === 'italian' || v === 'english';
}

/** Openings offered for a variant: ballots only where an official table exists. */
export function openingModesFor(variant: string): readonly OpeningMode[] {
  return variantHasBallot(variant)
    ? OPENING_MODES
    : OPENING_MODES.filter((mode) => ballotCeiling(mode) === null);
}

/**
 * The opening a tournament/arena row USED to describe with two columns.
 *
 * Competitions carried `first_move_lottery` (draw or not?) plus `ballot_ceiling`
 * (from which federal table), which migration 101 replaced with a single
 * `OpeningMode`. The pair still lives on `schedule_slots`: calendar bands are
 * written in the old spelling, so they get translated when a band opens a game.
 *
 * `ceiling` is nullable there and on `arenas` — absent meant "whatever the
 * drawer defaults to", i.e. table B — so the fallback is 2, exactly as
 * `createArenaGameRoom` did with its `ballotCeiling: BallotCeiling = 2`.
 */
export function openingModeFromLegacy(
  firstMoveLottery: boolean | null | undefined,
  ceiling: number | null | undefined,
): OpeningMode {
  if (!firstMoveLottery) return 'free';
  const c = ceiling ?? 2;
  return c >= 1 && c <= 4 ? (`ballot${c}` as OpeningMode) : 'ballot2';
}

/**
 * Legacy opening spellings, still arriving from cached web bundles and from every
 * device that saved a "last used" preference. They are mapped to the mode that
 * draws the SAME pool they used to, never to the same-looking name:
 *  - `drawn` / `drawn3` meant "3 half-moves", which on Italian/English drew from
 *    federal table B — that pool is now `ballot2`, not the uncapped `random3`;
 *  - `drawn2` was always 2 random plies, a pure rename.
 */
const LEGACY_OPENING_MODES: Readonly<Record<string, OpeningMode>> = {
  drawn: 'ballot2',
  drawn3: 'ballot2',
  drawn2: 'random2',
};

/**
 * Narrow an untrusted opening string (wire payload / localStorage) to a mode a
 * CHALLENGE can play, falling back to the default.
 *
 * ⚠️ Checks `OPENING_MODES`, the eight — so `'single'` arriving here becomes
 * `'free'`, and that is the point, not an oversight.
 *
 * Its only callers are the direct challenges / lobby tables
 * (`normalizeChallengeSettings`) and the "last used" preference saved on the
 * device (`last-used-play.ts`). NEITHER of them carries an opening id. Were this
 * list widened to nine, a stale client — or a preference saved from a tournament
 * form — could land `'single'` on a challenge, where `applyDrawnOpening` finds no
 * ceiling and falls through to three RANDOM half-moves: an opening drawn by lot,
 * announced as one chosen by hand. Degrading to `'free'` is the honest answer.
 *
 * Tournaments do not come through this door: they come through their own zod
 * schema, which declares the ninth value on purpose and beside the number.
 */
export function normalizeOpeningMode(raw: string | undefined | null): OpeningMode {
  const legacy = typeof raw === 'string' ? LEGACY_OPENING_MODES[raw] : undefined;
  if (legacy) return legacy;
  return OPENING_MODES.includes(raw as OpeningMode) ? (raw as OpeningMode) : DEFAULT_OPENING_MODE;
}

/**
 * Degrade a ballot mode to random plies on a variant that has no ballot table, so
 * a client (or an old saved preference) asking for `ballot3` on Frisian gets a
 * drawn opening instead of a silent free game.
 */
export function resolveOpeningMode(opening: OpeningMode, variant: string): OpeningMode {
  /* `single` degrades to FREE, not to random plies, and the difference matters.
     A `ballot3` asked on Frisian still means "draw me something": there is no
     table, so three legal half-moves at random keep the promise as closely as it
     can be kept. A chosen opening cannot be approximated — the opening the
     organiser picked does not exist on a board with no table — and drawing one at
     random in its place would be a different opening playing under its name. */
  if (opening === 'single' && !variantHasBallot(variant)) return 'free';
  if (ballotCeiling(opening) !== null && !variantHasBallot(variant)) return 'random3';
  return opening;
}

/**
 * Openings a TOURNAMENT offers: the eight of a challenge, plus `single` in last
 * place wherever an official table exists to choose from.
 *
 * Last, and not by taste: reading the dropdown down, every entry constrains the
 * start less than the one above it — and this one constrains it completely. It is
 * the far end of that same line, not a category of its own.
 */
export function tournamentOpeningModesFor(variant: string): readonly OpeningMode[] {
  const offerte = openingModesFor(variant);
  return variantHasBallot(variant) ? [...offerte, 'single'] : offerte;
}
